import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, CheckCircle2, Download, Share } from 'lucide-react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { useFamilyGroupStore } from '../../store/familyGroupStore';
import { getDeferredPrompt, triggerInstall, isStandalone, isMobileDevice, isIOS as isIOSDevice } from '../../lib/pwaInstall';

interface ProfileData {
  firstName: string;
  lastName: string;
  secondLastName: string;
}

interface ProfileSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ isOpen, onClose }) => {
  const { user } = useAuthStore();
  const { getActiveFamily } = useFamilyGroupStore();
  const activeFamily = getActiveFamily();
  const [formData, setFormData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    secondLastName: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadProfile();
    }
  }, [isOpen, user]);

  const loadProfile = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, second_last_name')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setFormData({
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          secondLastName: data.second_last_name || ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setErrors({ general: 'Error al cargar el perfil' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage('');

    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El primer apellido es requerido';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          second_last_name: formData.secondLastName.trim() || null
        })
        .eq('id', user?.id);

      if (error) throw error;

      setSuccessMessage('Perfil actualizado exitosamente');
      setTimeout(() => {
        setSuccessMessage('');
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ general: 'Error al actualizar el perfil' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Información Personal" maxWidth="md">
      <p className="text-sm text-stone-600 -mt-4 mb-6">Actualiza tus datos personales</p>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
          </div>
        ) : (
          <>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-stone-700 mb-2">
                Nombre <span className="text-rose-500">*</span>
              </label>
              <input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 ${
                  errors.firstName ? 'border-rose-500 focus:ring-rose-500 focus:border-rose-500' : 'border-stone-300'
                }`}
                placeholder="Juan"
                required
              />
              {errors.firstName && (
                <p className="mt-2 text-sm text-rose-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.firstName}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-stone-700 mb-2">
                Primer Apellido <span className="text-rose-500">*</span>
              </label>
              <input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className={`w-full px-4 py-3 border rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 ${
                  errors.lastName ? 'border-rose-500 focus:ring-rose-500 focus:border-rose-500' : 'border-stone-300'
                }`}
                placeholder="Pérez"
                required
              />
              {errors.lastName && (
                <p className="mt-2 text-sm text-rose-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.lastName}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="secondLastName" className="block text-sm font-medium text-stone-700 mb-2">
                Segundo Apellido <span className="text-stone-400 text-xs">(Opcional)</span>
              </label>
              <input
                id="secondLastName"
                type="text"
                value={formData.secondLastName}
                onChange={(e) => setFormData({ ...formData, secondLastName: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                placeholder="García"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-4 py-3 border border-stone-300 rounded-lg shadow-sm bg-stone-50 text-stone-500 cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-stone-500">
                El email no puede ser modificado
              </p>
            </div>

            {activeFamily && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Rol en {activeFamily.name}
                </label>
                <input
                  type="text"
                  value={activeFamily.user_role}
                  disabled
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg shadow-sm bg-stone-50 text-stone-500 cursor-not-allowed capitalize"
                />
              </div>
            )}

            {errors.general && (
              <div className="p-3 rounded-lg bg-red-50 text-red-800 flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{errors.general}</span>
              </div>
            )}

            {successMessage && (
              <div className="p-3 rounded-lg bg-green-50 text-green-800 flex items-start space-x-2">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{successMessage}</span>
              </div>
            )}

            <Button
              type="submit"
              loading={saving}
              icon={Save}
              iconPosition="left"
              fullWidth
            >
              Guardar Cambios
            </Button>
          </form>

          {/* Install Ethernal section */}
          {isMobileDevice() && !isStandalone() && (
            <div className="mt-6 pt-6 border-t border-stone-200">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 bg-rose-100 rounded-lg flex items-center justify-center">
                  <Download className="h-4 w-4 text-rose-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-stone-900">Instalar Ethernal</p>
                  <p className="text-xs text-stone-500 mt-0.5">Accede más fácil desde tu pantalla de inicio.</p>
                  {isIOSDevice() ? (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-stone-500">
                      <Share className="h-3.5 w-3.5 text-rose-500" />
                      <span>Toca <span className="font-semibold">Compartir</span> → <span className="font-semibold">"Añadir a pantalla de inicio"</span></span>
                    </div>
                  ) : getDeferredPrompt() ? (
                    <button
                      onClick={async () => { await triggerInstall(); }}
                      className="mt-2 bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold px-4 py-1.5 rounded-lg transition-colors"
                    >
                      Instalar
                    </button>
                  ) : (
                    <p className="mt-1 text-xs text-stone-400">
                      Abre el menú del navegador → "Instalar app"
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          </>
        )}
    </Modal>
  );
};

export default ProfileSettings;
